import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { environment } from '../../environments/environment';
import { VersionHistoryService } from './version-history.service';

export interface VersionReleaseNotes {
    title?: string;
    items?: string[];
}

export interface VersionManifest {
    version: string;
    releaseNotes?: VersionReleaseNotes;
}

@Injectable({
    providedIn: 'root'
})
export class VersionUpdateService implements OnDestroy {
    readonly versionKey = 'appVersion';
    readonly skipReloadPromptKey = 'skipReloadPrompt';

    private pollIntervalMs = environment.versionCheckIntervalMs ?? 10 * 60 * 1000;
    private readonly updateAvailableSubject = new BehaviorSubject<boolean>(false);
    private latestServerVersion: string | null = null;
    private latestReleaseNotes: VersionReleaseNotes | null = null;
    private bannerDismissed = false;
    private pollSubscription: Subscription | null = null;
    private visibilityHandler = () => this.onVisibilityChange();

    readonly updateAvailable$ = this.updateAvailableSubject.asObservable();

    constructor(
        private http: HttpClient,
        private versionHistoryService: VersionHistoryService
    ) {}

    /** Prod: poll on interval + when user returns to tab + once on app boot. */
    startMonitoring(): void {
        if (!environment.enableVersionMonitoring) {
            return;
        }

        console.log(`Version monitoring enabled (poll every ${this.pollIntervalMs / 1000}s)`);
        this.checkVersion(false);
        this.pollSubscription = interval(this.pollIntervalMs).subscribe(() => {
            this.bannerDismissed = false;
            this.checkVersion(true);
        });
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    /** Dev/UAT: single check after successful login (no background polling). */
    checkOnceOnLogin(): void {
        if (!environment.versionCheckOnLoginOnly) {
            return;
        }
        this.checkVersion(true);
    }

    ngOnDestroy(): void {
        this.pollSubscription?.unsubscribe();
        document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    dismissBanner(): void {
        this.bannerDismissed = true;
        this.updateAvailableSubject.next(false);
    }

    applyUpdate(): void {
        if (!this.latestServerVersion) {
            return;
        }

        sessionStorage.setItem(this.skipReloadPromptKey, 'true');
        localStorage.setItem(this.versionKey, this.latestServerVersion);
        location.reload();
    }

    getLatestServerVersion(): string | null {
        return this.latestServerVersion;
    }

    getLatestReleaseNotes(): VersionReleaseNotes | null {
        return this.latestReleaseNotes;
    }

    private onVisibilityChange(): void {
        if (document.visibilityState === 'visible') {
            this.checkVersion(false);
        }
    }

    private checkVersion(fromScheduledPoll: boolean): void {
        const cacheBuster = Date.now();
        this.http.get<VersionManifest>(`/assets/version.json?t=${cacheBuster}`).subscribe({
            next: (res) => {
                const serverVersion = res.version;
                this.latestServerVersion = serverVersion;
                this.latestReleaseNotes = this.normalizeReleaseNotes(res.releaseNotes);

                const storedVersion = localStorage.getItem(this.versionKey);

                if (!storedVersion) {
                    localStorage.setItem(this.versionKey, serverVersion);
                    this.updateAvailableSubject.next(false);
                    this.registerBuildIfAuthenticated(serverVersion);
                    return;
                }

                if (storedVersion !== serverVersion) {
                    if (this.bannerDismissed && !fromScheduledPoll) {
                        return;
                    }
                    this.updateAvailableSubject.next(true);
                    return;
                }

                this.bannerDismissed = false;
                this.updateAvailableSubject.next(false);
                this.registerBuildIfAuthenticated(serverVersion);
            },
            error: (err) => console.warn('Version check failed:', err)
        });
    }

    private normalizeReleaseNotes(notes?: VersionReleaseNotes): VersionReleaseNotes | null {
        if (!notes) {
            return null;
        }

        const title = notes.title?.trim() ?? '';
        const items = (notes.items ?? []).map((item) => item.trim()).filter(Boolean);

        if (!title && items.length === 0) {
            return null;
        }

        return {
            title: title || undefined,
            items: items.length > 0 ? items : undefined
        };
    }

    private registerBuildIfAuthenticated(buildVersion: string): void {
        if (!localStorage.getItem('auth_token')) {
            return;
        }
        this.versionHistoryService.registerBuild(buildVersion).subscribe();
    }
}
